import type { WidgetConfigState } from './fields';

/**
 * "Build your own" metadata: the endpoints behind a widget, so a partner who wants their own
 * rendering can read the same data instead of reverse-engineering the embed.
 *
 * Deliberately points at surfaces we are willing to support rather than at whatever the widget
 * happens to call internally. Several widgets read widget-app routes (`/api/widgets/*`) that
 * carry no stability guarantee; documenting those would turn them into a partner contract, the
 * same trap as the unversioned embed loader.
 *
 * **Everything here must stay JSON-serializable.** The `[category]/[item]` page is a Server
 * Component and hands the whole schema to the client `WidgetWorkbench`, so a function on a
 * schema fails to cross the boundary and takes the entire configurator down with it, not just
 * this panel. That is why requests are templates resolved at render time rather than builder
 * functions. `schemaIsSerializable` in the tests holds the line.
 */

/** Public exchange API host. */
export const EXCHANGE_API_BASE = 'https://api.elections.kalshi.com';

/** Public API reference. */
export const KALSHI_DOCS_URL = 'https://docs.kalshi.com';

/**
 * Applies to every endpoint here, so the panel states it once rather than each widget
 * repeating it.
 *
 * The exchange rejects browser requests carrying a non-Kalshi `Origin` with a 403 (CORS plus an
 * AWS-WAF challenge; see the comment in `react_pages/AmericanPowerIndex/api/kpowApi.ts`).
 * Server-side calls send no `Origin` and are unaffected. Verified against production: no
 * `Origin` and `Origin: https://kalshi.com` both return 200, an arbitrary partner origin
 * returns 403.
 */
export const SERVER_SIDE_ONLY_NOTE =
    'The exchange rejects browser requests from non-Kalshi origins with a 403, so a fetch() straight from a partner page will fail.';

/**
 * The opposite case: routes this app serves itself send `Access-Control-Allow-Origin: *`
 * (`cacheHeaders` in `computeForwardCurveRoutes.ts`), so a partner page can call them
 * directly. Verified against production: a request carrying an arbitrary `Origin` returns 200
 * with `access-control-allow-origin: *`, where the exchange returns 403.
 */
export const BROWSER_CALLABLE_NOTE =
    'These endpoints send Access-Control-Allow-Origin: *, so you can fetch them straight from your page. No server proxy needed.';

export interface DataRequest {
    /** Short label for the call, e.g. "Current curve". */
    label: string;
    /**
     * URL carrying `{token}` placeholders, resolved against the live widget config by
     * `resolveDataRequests` so the sample matches what is being previewed.
     */
    url: string;
    /** One line on what comes back; may also carry placeholders. */
    returns: string;
}

export interface WidgetDataSource {
    /** One line on what a partner gets by calling this directly. */
    summary: string;
    requests: DataRequest[];
    /** Deep link into the public API reference for this surface. */
    docsUrl?: string;
    /** Endpoint-specific caveats: required params, caps, coverage gaps. */
    notes?: string[];
    /**
     * True when the endpoints allow cross-origin browser requests. Defaults to false, because
     * the exchange API is origin-allowlisted and 403s a partner page. Telling a partner to
     * proxy when they do not need to is its own kind of wrong answer.
     */
    browserCallable?: boolean;
}

/** Hourly KPOW history window the chart itself requests. */
const KPOW_HISTORY_DAYS = 180;
const HOUR_SECONDS = 3600;

/**
 * `start_ts` is required (the endpoint 400s without it) and is floored to the hour so the URL
 * is stable between reads. `now` is injectable to keep this testable.
 */
export function kpowHistoryStartTs(now: number = Date.now()): number {
    const startMs = now - KPOW_HISTORY_DAYS * 24 * HOUR_SECONDS * 1000;
    return Math.floor(startMs / 1000 / HOUR_SECONDS) * HOUR_SECONDS;
}

const asString = (value: unknown): string | undefined =>
    typeof value === 'string' && value.trim() ? value.trim() : undefined;

/**
 * First selected market.
 *
 * A `market-select` field always stores a `string[]`, whichever key it uses: the watchlist
 * calls it `marketIds`, symbol detail calls it `marketId`. Reading only one of those as an
 * array meant symbol detail's single-market example was always dropped.
 */
const firstMarket = (config: WidgetConfigState): string | undefined => {
    for (const value of [config.marketIds, config.marketId]) {
        const first = Array.isArray(value) ? asString(value[0]) : asString(value);
        if (first) return first;
    }
    return undefined;
};

/**
 * Placeholder values for the current config. `undefined` means "cannot be filled", and any
 * request referencing that token is dropped rather than rendered with a broken URL, which is
 * how the optional single-market example disappears when nothing is selected.
 */
function tokensFor(config: WidgetConfigState, widgetsOrigin: string, now?: number): Record<string, string | undefined> {
    const market = firstMarket(config);
    return {
        origin: widgetsOrigin,
        // Defaults keep the documented example valid before the user touches anything.
        eventTicker: encodeURIComponent(asString(config.eventTicker) ?? 'KXPRESPARTY-28'),
        chip: encodeURIComponent(asString(config.chip) ?? 'b200'),
        category: asString(config.category) ?? 'Politics',
        kpowHistoryStart: String(kpowHistoryStartTs(now)),
        // No fallback: a market example is only meaningful once one is selected.
        marketId: market && encodeURIComponent(market),
    };
}

const PLACEHOLDER = /\{(\w+)\}/g;

/**
 * Fills a template, or returns undefined if any placeholder has no value.
 * Exported for tests; the panel goes through `resolveDataRequests`.
 */
export function fillTemplate(template: string, tokens: Record<string, string | undefined>): string | undefined {
    let missing = false;
    const filled = template.replace(PLACEHOLDER, (_match, key: string) => {
        const value = tokens[key];
        if (value === undefined) {
            missing = true;
            return '';
        }
        return value;
    });
    return missing ? undefined : filled;
}

/** The requests to show for a widget, with placeholders resolved against its current config. */
export function resolveDataRequests(
    source: WidgetDataSource,
    config: WidgetConfigState,
    widgetsOrigin: string,
    now?: number,
): DataRequest[] {
    const tokens = tokensFor(config, widgetsOrigin, now);
    return source.requests.flatMap((request) => {
        const url = fillTemplate(request.url, tokens);
        if (url === undefined) return [];
        return [{ ...request, url, returns: fillTemplate(request.returns, tokens) ?? request.returns }];
    });
}

// --- Reusable request sets -------------------------------------------------

/**
 * Event plus its nested markets: prices, strikes and volumes for one event. This is the
 * public v2 equivalent of what the event-backed widgets render.
 */
export const EVENT_REQUESTS: DataRequest[] = [
    {
        label: 'Event with markets',
        url: `${EXCHANGE_API_BASE}/trade-api/v2/events/{eventTicker}?with_nested_markets=true`,
        returns: 'The event plus every market on it, with current yes/no prices, strikes and volume.',
    },
    {
        label: 'Single market',
        url: `${EXCHANGE_API_BASE}/trade-api/v2/markets/{marketId}`,
        returns: 'One market on its own, if you only need the contract this widget highlights.',
    },
];

export const KPOW_REQUESTS: DataRequest[] = [
    {
        label: 'Current index',
        url: `${EXCHANGE_API_BASE}/v1/cached/indexes/KPOW`,
        returns: 'The latest composite score, the leading party, and each weighted component.',
    },
    {
        label: 'History',
        url: `${EXCHANGE_API_BASE}/v1/cached/indexes/KPOW/history?start_ts={kpowHistoryStart}`,
        returns: 'Hourly composite points from start_ts to now, which is what the chart line plots.',
    },
];

/** The compute forward curve is served by this app, and is the same data Bloomberg reads. */
export const COMPUTE_REQUESTS: DataRequest[] = [
    {
        label: 'Forward curve',
        url: '{origin}/api-app/compute/forward-curve/{chip}',
        returns: 'Market-implied GPU-hour prices across the weekly and monthly forward points.',
    },
    {
        label: 'Realized history',
        url: '{origin}/api-app/compute/forward-curve/{chip}/history',
        returns: 'Realized spot prices behind the solid part of the line.',
    },
];

/**
 * Ticker tape: a page of open events the partner groups themselves.
 *
 * The public endpoint has no category filter, so the recipe is "page through open events and
 * filter client-side". `limit` is the maximum rather than a token page, because a small page
 * can easily contain none of the category you want.
 */
export const TICKER_TAPE_REQUESTS: DataRequest[] = [
    {
        label: 'Open events',
        url: `${EXCHANGE_API_BASE}/trade-api/v2/events?status=open&limit=200&with_nested_markets=true`,
        returns: 'A page of open events with their markets. Each event carries a `category` field to filter on.',
    },
];
